import React, { useMemo, useCallback, useContext } from "react";
import Grid from "@mui/material/Grid";
import HtmlContentLoader from "@shared/HtmlContentLoader";
import DynamicHtml from "@common/DynamicHtml";
import CommonService from "@services/CommonService";
import {ChannelList} from "./sections";
import LoaderWithIcon from "@common/LoaderWithIcon";
import { PAGE } from "../contexts/PageNavigationContext";
import { usePageConfiguration } from "../contexts/PageConfigurationContext";
import { SearchContext } from "@contexts/Search";
import Helper from "../Helper";
import { CONSTANT } from "../constant";
import { useMediaQuery } from "@mui/material";
import { PendoService } from "@services";

const DEFAULT_COLUMN_COUNT = "3";
const DEFAULT_ALIGNMENT = "left aligned";

const DEFAULT_CONFIG = {
  channels: [],
  headerText: "",
  subHeader: "",
  channelAlignment: DEFAULT_ALIGNMENT,
  channelColumnCount: DEFAULT_COLUMN_COUNT,
  customFooter: "",
  sideHtmlConfig:  {
        sideHtmlDesktopUrl: "",
        sideHtmlMobileUrl: "",
        sideHtmlDesktopPosition: "",
        sideHtmlMobilePosition: "",
        sideHtmlPercentWidth: CONSTANT.DEFAULT_DESKTOP_SIDE_HTML_WIDTH_PERCENT
      }
};


const useChannelConfiguration = (pageConfig) => {
  return useMemo(() => {
    if (!pageConfig?.channelSelectionPage || !pageConfig?.generalSetting) {
      return DEFAULT_CONFIG;
    }

    const { channels = [], channelSelectionPage } = pageConfig;

    const {
      header = "",
      subheader = "",
      footer = "",
      channelColumnCount = DEFAULT_COLUMN_COUNT,
      channelAlignment = DEFAULT_ALIGNMENT,
      htmlPosition = CONSTANT.DEFAULT_DESKTOP_SIDE_HTML_ALIGNMENT,
      htmlScreenSizePercent = CONSTANT.DEFAULT_DESKTOP_SIDE_HTML_WIDTH_PERCENT,
      htmlFile = "",
      mobileHtmlFile = "",
      mobileHtmlPosition = CONSTANT.DEFAULT_MOBILE_SIDE_HTML_ALIGNMENT,
    } = channelSelectionPage;


    return {
      channels,
      headerText: header,
      subHeader: subheader,
      channelAlignment,
      channelColumnCount,
      customFooter: footer,
      sideHtmlConfig: {
        sideHtmlDesktopUrl: htmlFile,
        sideHtmlMobileUrl: mobileHtmlFile,
        sideHtmlDesktopPosition: htmlPosition,
        sideHtmlMobilePosition: mobileHtmlPosition,
        sideHtmlPercentWidth: htmlScreenSizePercent
      }
    };
  }, [pageConfig]);
};

const ChannelSelectionPage = () => {
  const { setSelectedChannel, getPageConfig, getMobileBreakpoint } = usePageConfiguration();
  const pageConfig = getPageConfig(PAGE.CHANNEL_SELECTION);
  const config = useChannelConfiguration(pageConfig);
  const isMobile = useMediaQuery(getMobileBreakpoint());
  const { channels, headerText, subHeader, channelColumnCount, channelAlignment, customFooter, sideHtmlConfig } = config;
  const { productId, productName, indicationName } = useContext(SearchContext);

  // Get streamlined side HTML configuration
  const sideHtml = useMemo(() => {
    const config = Helper.getSideHtmlConfig(sideHtmlConfig, isMobile);
    return {
      ...config,
      htmlUrl: config.htmlUrl ? CommonService.getHtmlFileUrl(config.htmlUrl, pageConfig.clientId) : ""
    };
  }, [sideHtmlConfig, isMobile, pageConfig.clientId]);

  const handleChannelClick = useCallback(
    (channel) => {
      PendoService.getInstance().trackEvent("PF2 Channel Button Click", "ChannelSelectionPage", channel?.channelName, productId, productName, indicationName);
      setSelectedChannel(channel?.projectChannelId);
    },[setSelectedChannel]
  );

  const onAnchorClick = (url) => {
    PendoService.getInstance().trackEvent("PF2 Custom Link Click", "ChannelSelectionPage", url, productId, productName, indicationName);
  };

  if (!pageConfig) {
    return <LoaderWithIcon />;
  }

  return (
    <div className="norstella-pf-container">
      {/* HTML Panel */}
      {sideHtml.showHtml && (
        <div className="norstella-pf-left" style={{ width: sideHtml.width, order: sideHtml.htmlOrder }}>
          <HtmlContentLoader url={sideHtml.htmlUrl} onAnchorClick={onAnchorClick} />
        </div>
      )}

      {/* Channel Panel */}
      <div className={`norstella-pf-right ${sideHtml.containerClass}`} style={{ width: sideHtml.contentWidth, order: sideHtml.contentOrder }}>
        {headerText?.trim() && (
          <DynamicHtml part="pfDivSubHeaderText" input={headerText} className="norstella-pf-header" />
        )}

        {subHeader?.trim() && (
          <Grid item className="norstella-pf-GridItemSubHeader">
            <DynamicHtml input={subHeader} className="norstella-pf-headerSubHeader" part="pfDivSubHeaderText" />
          </Grid>
        )}

        {channels && (
          <ChannelList
            channelColumnCount={channelColumnCount}
            channelAlignment={channelAlignment}
            channels={channels}
            onChannelClick={handleChannelClick}
            isMobile={isMobile}
          />
        )}

        {customFooter && (
          <Grid item className="norstella-pf-footer-section">
            <DynamicHtml input={customFooter} className="norstella-pf-footer with-left-aligned" part="pfDivFooterText" />
          </Grid>
        )}
      </div>
    </div>
  );
};

export default ChannelSelectionPage;
